import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Wallet,
  ArrowLeftRight,
  DollarSign,
  ClipboardList,
  User,
  Bot,
  Activity,
  Database,
} from 'lucide-react';
import { adminApi, DashboardSystemStatus } from '../api/admin';
import { auditLogApi, AuditLogListItem } from '../api/auditLog';

interface DashboardStats {
  totalClients: number;
  activeAccounts: number;
  paymentsThisMonth: number;
  fxVolumeThisMonth: number;
  /** Month-over-month %; null when prior month was 0 but current &gt; 0 (undefined growth). */
  totalClientsChangePercent?: number | null;
  activeAccountsChangePercent?: number | null;
  paymentsChangePercent?: number | null;
  fxVolumeChangePercent?: number | null;
}

function momPercentDisplay(pct: number | null | undefined): { text: string; className: string } {
  if (pct === null || pct === undefined || Number.isNaN(pct))
    return { text: '—', className: 'text-gray-400' };
  if (pct === 0) return { text: '0%', className: 'text-gray-500' };
  const sign = pct > 0 ? '+' : '';
  const className = pct > 0 ? 'text-green-600' : 'text-red-600';
  return { text: `${sign}${pct}%`, className };
}

function relativeTimeShort(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffSec = Math.round((Date.now() - t) / 1000);
  if (diffSec < 10) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 14) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatUtcChecked(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' });
}

function statusTone(status: string, okValues: string[]): string {
  const s = status?.toLowerCase() ?? '';
  if (okValues.some((v) => v.toLowerCase() === s)) return 'text-green-600';
  if (s === 'error') return 'text-red-600';
  return 'text-amber-600';
}

function activityVisual(actorType: string) {
  const u = actorType?.toUpperCase() ?? '';
  if (u === 'ADMIN')
    return {
      Icon: User,
      wrap: 'bg-indigo-100',
      iconClass: 'text-indigo-600',
    };
  if (u === 'CLIENT')
    return {
      Icon: Users,
      wrap: 'bg-emerald-100',
      iconClass: 'text-emerald-600',
    };
  return {
    Icon: Bot,
    wrap: 'bg-slate-100',
    iconClass: 'text-slate-600',
  };
}

function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats>({
    totalClients: 0,
    activeAccounts: 0,
    paymentsThisMonth: 0,
    fxVolumeThisMonth: 0,
  });
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState<AuditLogListItem[]>([]);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [systemStatus, setSystemStatus] = useState<DashboardSystemStatus | null>(null);
  const [systemStatusError, setSystemStatusError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setActivityError(null);
        setSystemStatusError(null);

        const [statsResult, auditResult, statusResult] = await Promise.allSettled([
          adminApi.getDashboardStats(),
          auditLogApi.list({ page: 1, pageSize: 10 }),
          adminApi.getDashboardSystemStatus(),
        ]);
        if (cancelled) return;

        if (statsResult.status === 'fulfilled') {
          const d = statsResult.value.data as DashboardStats;
          setStats({
            totalClients: Number(d.totalClients) || 0,
            activeAccounts: Number(d.activeAccounts) || 0,
            paymentsThisMonth: Number(d.paymentsThisMonth) || 0,
            fxVolumeThisMonth: Number(d.fxVolumeThisMonth) || 0,
            totalClientsChangePercent: d.totalClientsChangePercent ?? null,
            activeAccountsChangePercent: d.activeAccountsChangePercent ?? null,
            paymentsChangePercent: d.paymentsChangePercent ?? null,
            fxVolumeChangePercent: d.fxVolumeChangePercent ?? null,
          });
        } else {
          console.error('Dashboard stats failed:', statsResult.reason);
          try {
            const statsResponse = await adminApi.getDashboardStats();
            if (!cancelled) {
              const d = statsResponse.data as DashboardStats;
              setStats({
                totalClients: Number(d.totalClients) || 0,
                activeAccounts: Number(d.activeAccounts) || 0,
                paymentsThisMonth: Number(d.paymentsThisMonth) || 0,
                fxVolumeThisMonth: Number(d.fxVolumeThisMonth) || 0,
                totalClientsChangePercent: d.totalClientsChangePercent ?? null,
                activeAccountsChangePercent: d.activeAccountsChangePercent ?? null,
                paymentsChangePercent: d.paymentsChangePercent ?? null,
                fxVolumeChangePercent: d.fxVolumeChangePercent ?? null,
              });
            }
          } catch {
            /* keep defaults */
          }
        }

        if (auditResult.status === 'fulfilled') {
          const auditPayload = auditResult.value;
          const data = auditPayload?.data;
          if (!auditPayload || auditPayload.isSuccess === false || !data) {
            setActivity([]);
            setActivityError(
              typeof auditPayload?.error === 'string'
                ? auditPayload.error
                : 'Could not load audit trail'
            );
          } else {
            setActivity(Array.isArray(data.items) ? data.items : []);
          }
        } else {
          console.error('Audit log failed:', auditResult.reason);
          setActivity([]);
          setActivityError('Could not load recent activity');
        }

        if (statusResult.status === 'fulfilled') {
          setSystemStatus(statusResult.value.data);
        } else {
          console.error('System status failed:', statusResult.reason);
          setSystemStatus(null);
          setSystemStatusError('Could not load system status');
        }
      } catch (error) {
        console.error('Failed to fetch dashboard:', error);
        if (!cancelled) {
          try {
            const statsResponse = await adminApi.getDashboardStats();
            const d = statsResponse.data as DashboardStats;
            setStats({
              totalClients: Number(d.totalClients) || 0,
              activeAccounts: Number(d.activeAccounts) || 0,
              paymentsThisMonth: Number(d.paymentsThisMonth) || 0,
              fxVolumeThisMonth: Number(d.fxVolumeThisMonth) || 0,
              totalClientsChangePercent: d.totalClientsChangePercent ?? null,
              activeAccountsChangePercent: d.activeAccountsChangePercent ?? null,
              paymentsChangePercent: d.paymentsChangePercent ?? null,
              fxVolumeChangePercent: d.fxVolumeChangePercent ?? null,
            });
          } catch {
            /* keep defaults */
          }
          setActivity([]);
          setActivityError('Could not load recent activity');
          setSystemStatus(null);
          setSystemStatusError('Could not load system status');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Overview of XchangeLab Ledger system</p>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  const clientsMom = momPercentDisplay(stats.totalClientsChangePercent);
  const accountsMom = momPercentDisplay(stats.activeAccountsChangePercent);
  const paymentsMom = momPercentDisplay(stats.paymentsChangePercent);
  const fxMom = momPercentDisplay(stats.fxVolumeChangePercent);

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Overview of XchangeLab Ledger system</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <span
              className={`text-sm font-medium tabular-nums ${clientsMom.className}`}
              title="New client registrations vs previous calendar month"
            >
              {clientsMom.text}
            </span>
          </div>
          <div className="mt-4">
            <h3 className="text-2xl font-semibold text-gray-900">{stats.totalClients.toLocaleString()}</h3>
            <p className="text-sm text-gray-600">Total Clients</p>
            <p className="text-xs text-gray-400 mt-0.5">Δ vs prev. month (new signups)</p>
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-100">
              <Wallet className="h-6 w-6 text-green-600" />
            </div>
            <span
              className={`text-sm font-medium tabular-nums ${accountsMom.className}`}
              title="ACTIVE client accounts opened vs previous calendar month"
            >
              {accountsMom.text}
            </span>
          </div>
          <div className="mt-4">
            <h3 className="text-2xl font-semibold text-gray-900">{stats.activeAccounts.toLocaleString()}</h3>
            <p className="text-sm text-gray-600">Active Accounts</p>
            <p className="text-xs text-gray-400 mt-0.5">Δ vs prev. month (new accounts)</p>
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-100">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
            <span
              className={`text-sm font-medium tabular-nums ${paymentsMom.className}`}
              title="Outgoing payments created vs previous calendar month"
            >
              {paymentsMom.text}
            </span>
          </div>
          <div className="mt-4">
            <h3 className="text-2xl font-semibold text-gray-900">{stats.paymentsThisMonth.toLocaleString()}</h3>
            <p className="text-sm text-gray-600">Payments This Month</p>
            <p className="text-xs text-gray-400 mt-0.5">Δ vs prev. month</p>
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-100">
              <ArrowLeftRight className="h-6 w-6 text-orange-600" />
            </div>
            <span
              className={`text-sm font-medium tabular-nums ${fxMom.className}`}
              title="FX ledger volume vs previous calendar month"
            >
              {fxMom.text}
            </span>
          </div>
          <div className="mt-4">
            <h3 className="text-2xl font-semibold text-gray-900">${(stats.fxVolumeThisMonth / 1000000).toFixed(2)}M</h3>
            <p className="text-sm text-gray-600">FX Volume This Month</p>
            <p className="text-xs text-gray-400 mt-0.5">Δ vs prev. month</p>
          </div>
        </div>
      </div>

      {/* Recent Activity — from audit log API */}
      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
            <Link to="/audit-log" className="text-sm text-blue-600 hover:text-blue-800">
              Audit log →
            </Link>
          </div>
          {activityError ? (
            <p className="text-sm text-amber-700">{activityError}</p>
          ) : activity.length === 0 ? (
            <p className="text-sm text-gray-500">No audit events yet.</p>
          ) : (
            <div className="space-y-4">
              {activity.map((row) => {
                const { Icon, wrap, iconClass } = activityVisual(row.actorType);
                const subtitle =
                  row.description?.trim() ||
                  [row.entityType, row.entityId?.slice(0, 8)].filter(Boolean).join(' · ') ||
                  row.actorEmail ||
                  '—';
                return (
                  <div key={row.id} className="flex items-start gap-4">
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${wrap}`}>
                      <Icon className={`h-5 w-5 ${iconClass}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{row.action}</p>
                      <p className="text-xs text-gray-500 line-clamp-2">{subtitle}</p>
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">{relativeTimeShort(row.timestamp)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">System Status</h2>
          {systemStatusError ? (
            <p className="text-sm text-amber-700">{systemStatusError}</p>
          ) : !systemStatus ? (
            <p className="text-sm text-gray-500">No status data.</p>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <Activity className="h-4 w-4 text-slate-500 shrink-0" />
                  API
                </span>
                <div className="text-right text-sm">
                  <span
                    className={`font-medium capitalize ${statusTone(systemStatus.api.status, ['operational'])}`}
                  >
                    {systemStatus.api.status}
                  </span>
                  {systemStatus.api.checkedAtUtc ? (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatUtcChecked(systemStatus.api.checkedAtUtc)}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="flex items-start justify-between gap-3">
                <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <Database className="h-4 w-4 text-slate-500 shrink-0" />
                  Database
                </span>
                <div className="text-right text-sm">
                  <span
                    className={`font-medium capitalize ${statusTone(systemStatus.database.status, ['connected'])}`}
                  >
                    {systemStatus.database.status}
                  </span>
                  {systemStatus.database.latencyMs != null ? (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {systemStatus.database.latencyMs} ms ping
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="flex items-start justify-between gap-3">
                <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-slate-500 shrink-0" />
                  Audit trail
                </span>
                <div className="text-right text-sm">
                  <span
                    className={`font-medium capitalize ${statusTone(systemStatus.auditTrail.status, ['live'])}`}
                  >
                    {systemStatus.auditTrail.status}
                  </span>
                  {systemStatus.auditTrail.totalEvents != null ? (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {systemStatus.auditTrail.totalEvents.toLocaleString()} events
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
